import * as React from 'react'
import { create } from 'zustand'
import _ from 'lodash'
import { format, isBefore } from 'date-fns'
import useAppStore from '@/store/useAppStore'
import { BUDGETING_PERIODS } from '@/data/constants/constants'
import useGetVaults from '@/services/api/vault/useGetVaults'
import useGetPeriodColumns from '@/services/api/cash-flow/useGetPeriodColumns'
import useGetCashflowPeriods from '@/services/api/cash-flow/useGetCashflowPeriods'
import useGetIncomeCategories from '@/services/api/transaction-category/useGetIncomeCategories'
import useGetExpenseCategories from '@/services/api/transaction-category/useGetExpenseCategories'
import { useFeatureValue } from '@/components/feature-gate'
import { maxHorizonPeriods } from '@/utils/horizonLimits'
import usePersistentState from './usePersistentState'
import {
  buildTableRows,
  scopeVaults,
  type PeriodAggregate,
} from './tableRows'

/**
 * First visible period, as a column offset (0 = the current week/month).
 * Module-level rather than component state so it survives the table
 * remounting when a form route opens over it.
 */
const useCursorStore = create<{ cursor: number; setCursor: (c: number) => void }>(
  (set) => ({ cursor: 0, setCursor: (cursor) => set({ cursor }) }),
)

// The horizon picker only offers these counts (see HorizonMenu).
const WEEK_OPTIONS = [13, 26, 52]
const MONTH_OPTIONS = [6, 12, 24]
const WEEK_TO_MONTH = { 13: 6, 26: 6, 52: 12 }
const MONTH_TO_WEEK = { 6: 26, 12: 52, 24: 52 }

function fitHorizon(wanted: number, options: number[], max: number) {
  if (wanted <= max) return wanted
  const allowed = options.filter((o) => o <= max)
  return allowed.length ? allowed[allowed.length - 1] : options[0]
}

export interface PeriodHead {
  offset: number
  number?: number
  label: string
  sub: string
  /** sub without the year — fits a tablet column heading */
  short: string
  isNow: boolean
}

function rangeLabel(start: Date, end: Date) {
  return start.getMonth() === end.getMonth()
    ? `${format(start, 'd')} – ${format(end, 'd MMM yyyy')}`
    : `${format(start, 'd MMM')} – ${format(end, 'd MMM yyyy')}`
}

function shortRange(start: Date, end: Date) {
  return start.getMonth() === end.getMonth()
    ? `${format(start, 'd')}–${format(end, 'd MMM')}`
    : `${format(start, 'd MMM')}–${format(end, 'd MMM')}`
}

export default function useMobileTable({ visible }: { visible: number }) {
  const periodType = useAppStore((s) => s.periodType)
  const offset = useAppStore((s) => s.offset)
  const numberOfPeriods = useAppStore((s) => s.numberOfPeriods)
  const setOffset = useAppStore((s) => s.setOffset)
  const setPeriodType = useAppStore((s) => s.setPeriodType)
  const setNumberOfPeriods = useAppStore((s) => s.setNumberOfPeriods)
  const cursor = useCursorStore((s) => s.cursor)
  const setCursor = useCursorStore((s) => s.setCursor)

  const isWeek = periodType === BUDGETING_PERIODS.WEEK
  const args = { offset, numberOfPeriods, periodType }

  // Same query keys as the desktop grid, so switching layouts reuses the cache.
  const { data: vaults, status: vaultsStatus } = useGetVaults(args)
  const { data: columns, status: columnsStatus } = useGetPeriodColumns(args)
  const { data: cashflowPeriods, status: cashflowStatus } =
    useGetCashflowPeriods(args)
  const { data: income, status: incomeStatus } = useGetIncomeCategories()
  const { data: expense, status: expenseStatus } = useGetExpenseCategories()

  const [collapsed, setCollapsed] = usePersistentState<Record<string, boolean>>(
    'pf:mobile-table:collapsed',
    {},
  )
  const [scope, setScope] = usePersistentState<number[] | null>(
    'pf:mobile-table:vault-scope',
    null,
  )

  const allScopeVaults = React.useMemo(() => scopeVaults(vaults), [vaults])
  // Ignore stale ids (archived or from another team) and treat "all of them"
  // as no scope at all, so a newly added vault shows up by default.
  const selectedVaultIds = React.useMemo(() => {
    if (!scope) return null
    const live = allScopeVaults.filter((v) => scope.includes(v.id))
    if (!live.length || live.length === allScopeVaults.length) return null
    return new Set(live.map((v) => v.id))
  }, [scope, allScopeVaults])

  const periodByOffset = React.useMemo(() => {
    const map = new Map<number, PeriodAggregate>()
    ;(cashflowPeriods ?? []).forEach((p) => map.set(Number(p.offset), p))
    return map
  }, [cashflowPeriods])

  const minOffset = columns?.[0]?.offset ?? 0
  const maxOffset = columns?.[columns.length - 1]?.offset ?? 0
  // Clamp: a horizon change can leave the cursor past the last column.
  const first = Math.max(minOffset, Math.min(cursor, maxOffset - visible + 1))

  const visibleColumns = React.useMemo(
    () =>
      (columns ?? []).filter(
        (c) => c.offset >= first && c.offset < first + visible,
      ),
    [columns, first, visible],
  )
  const offsets = visibleColumns.map((c) => Number(c.offset))

  const heads: PeriodHead[] = visibleColumns.map((c) => {
    const start = new Date(c.start)
    const end = new Date(c.end)
    const isNow = Number(c.offset) === 0
    return {
      offset: Number(c.offset),
      number: c.number,
      isNow,
      label: isNow
        ? isWeek
          ? 'This week'
          : 'This month'
        : isWeek
          ? format(start, 'd MMM')
          : format(start, 'MMM'),
      sub: isWeek ? rangeLabel(start, end) : format(start, 'MMMM yyyy'),
      short: isWeek ? shortRange(start, end) : format(start, 'MMMM'),
    }
  })

  const rows = React.useMemo(
    () =>
      buildTableRows({
        vaults: vaults ?? [],
        incomeCategories: income ?? [],
        expenseCategories: expense ?? [],
        periodByOffset,
        offsets,
        selectedVaultIds,
        collapsed,
      }),
    // offsets is derived from visibleColumns
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vaults, income, expense, periodByOffset, visibleColumns, selectedVaultIds, collapsed],
  )

  // ---- Paging -------------------------------------------------------------
  const historyMonths = useFeatureValue('history.months', Infinity)
  const maxHistory = maxHorizonPeriods(isWeek ? 'Week' : 'Month', historyMonths)
  const [historyUpgradeOpen, setHistoryUpgradeOpen] = React.useState(false)

  // Same guard as TableControlRow: don't page back past the oldest vault's
  // opening balance.
  const oldestVaultDate = _.minBy(
    _.flatten((vaults ?? []).map((v) => (v.is_group ? v.vaults : [v]))),
    (v: any) => new Date(v?.opening_balance?.transaction_date ?? Date.now()),
  )?.opening_balance?.transaction_date
  const atOldest =
    !!oldestVaultDate &&
    !!columns?.[0]?.start &&
    isBefore(new Date(columns[0].start), new Date(oldestVaultDate))

  const canPrev = first > minOffset || !atOldest
  const canNext = first + visible - 1 < maxOffset

  const prev = () => {
    if (first > minOffset) {
      setCursor(first - 1)
      return
    }
    if (atOldest) return
    if (offset >= maxHistory) {
      setHistoryUpgradeOpen(true)
      return
    }
    // Load more history, like "Show Previous" on the desktop control row.
    setOffset(Number(offset) + visible)
    setCursor(first - 1)
  }
  const next = () => canNext && setCursor(first + 1)
  const goToday = () => setCursor(0)

  const forecastMonths = useFeatureValue('forecast.months', Infinity)
  const setMode = (mode: 'week' | 'month') => {
    if ((mode === 'week') === isWeek) return
    const nextCount =
      mode === 'month'
        ? fitHorizon(
            WEEK_TO_MONTH[numberOfPeriods] ?? 6,
            MONTH_OPTIONS,
            maxHorizonPeriods('Month', forecastMonths),
          )
        : fitHorizon(
            MONTH_TO_WEEK[numberOfPeriods] ?? 26,
            WEEK_OPTIONS,
            maxHorizonPeriods('Week', forecastMonths),
          )
    setNumberOfPeriods(nextCount)
    setPeriodType(mode === 'week' ? BUDGETING_PERIODS.WEEK : BUDGETING_PERIODS.MONTH)
    setCursor(0)
  }

  const toggleRow = (key: string) =>
    setCollapsed((c) => ({ ...c, [key]: !c[key] }))

  const toggleVault = (id: number) => {
    const current = selectedVaultIds
      ? [...selectedVaultIds]
      : allScopeVaults.map((v) => v.id)
    const nextIds = current.includes(id)
      ? current.filter((x) => x !== id)
      : [...current, id]
    // Never scope down to nothing — an empty table is never what was meant.
    if (nextIds.length) setScope(nextIds)
  }
  const allSelected = !selectedVaultIds
  const toggleAllVaults = () =>
    setScope(allSelected ? allScopeVaults.slice(0, 1).map((v) => v.id) : null)

  const structureLoading = [
    vaultsStatus,
    columnsStatus,
    incomeStatus,
    expenseStatus,
  ].some((s) => s === 'pending')
  const isLoading =
    structureLoading ||
    (cashflowStatus === 'pending' && !(cashflowPeriods?.length > 0)) ||
    offsets.length === 0

  return {
    isLoading,
    isWeek,
    setMode,
    heads,
    offsets,
    rows,
    prev,
    next,
    goToday,
    canPrev,
    canNext,
    historyUpgradeOpen,
    setHistoryUpgradeOpen,
    toggleRow,
    periodByOffset,
    scope: {
      vaults: allScopeVaults,
      isSelected: (id: number) => !selectedVaultIds || selectedVaultIds.has(id),
      toggle: toggleVault,
      toggleAll: toggleAllVaults,
      allSelected,
      label: allSelected
        ? 'All Vaults'
        : `${selectedVaultIds.size}/${allScopeVaults.length} Vaults`,
    },
  }
}

export type MobileTable = ReturnType<typeof useMobileTable>
